var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useRef, useState } from 'react';
import { useEvent, useFocusTrap, useOnEscape, useOutsideClick, usePreventDocumentScrolling, } from '@anton.bobrov/react-hooks';
import { useTimeline } from '@anton.bobrov/react-vevet-hooks';
import { Portal } from 'react-portal';
import cn from 'classnames';
import { prefixedClasNames } from '../../utils/prefixedClassNames';
import { CloseButton as DefaultCloseButton } from './CloseButton';
import { renderModalAnimation } from './utils';
/** Base Modal */
export var BaseModal = function (_a) {
    var className = _a.className, style = _a.style, isOpen = _a.isOpen, children = _a.children, parentNode = _a.parentNode, onOpen = _a.onOpen, onClose = _a.onClose, onHiddenProp = _a.onHidden, _b = _a.isUnderneathScrollingDisabled, isUnderneathScrollingDisabled = _b === void 0 ? false : _b, _c = _a.isRestoreFocusOnClose, isRestoreFocusOnClose = _c === void 0 ? true : _c, _d = _a.isCloseOnOutsideClick, isCloseOnOutsideClick = _d === void 0 ? true : _d, _e = _a.isUnmountOnClose, isUnmountOnClose = _e === void 0 ? true : _e, _f = _a.renderCloseButton, CloseButton = _f === void 0 ? DefaultCloseButton : _f, _g = _a.renderAnimation, renderAnimationProp = _g === void 0 ? renderModalAnimation : _g, _h = _a.duration, duration = _h === void 0 ? 350 : _h, wrapperProps = _a.wrapperProps, scrollAreaProps = _a.scrollAreaProps, overlayProps = _a.overlayProps;
    var _j = useState(false), canRender = _j[0], setCanRender = _j[1];
    var _k = useState(false), isVisible = _k[0], setIsVisible = _k[1];
    var onOpenEvent = useEvent(onOpen);
    var parentRef = useRef(null);
    var overlayRef = useRef(null);
    var scrollRef = useRef(null);
    var wrapperRef = useRef(null);
    var renderAnimation = useEvent(renderAnimationProp);
    var onHidden = useEvent(onHiddenProp);
    useEffect(function () {
        if (isOpen) {
            onOpenEvent === null || onOpenEvent === void 0 ? void 0 : onOpenEvent();
        }
    }, [isOpen, onOpenEvent]);
    useOnEscape(function () {
        if (isOpen) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    }, { ref: parentRef, isDisabled: !isVisible });
    useOutsideClick(wrapperRef, function () {
        if (isOpen) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    }, { isDisabled: !isCloseOnOutsideClick });
    usePreventDocumentScrolling(isUnderneathScrollingDisabled && isOpen);
    useFocusTrap(parentRef, {
        isDisabled: !isOpen,
        isRestoreFocus: isRestoreFocusOnClose,
    });
    var timeline = useTimeline({
        duration: duration,
        onProgress: function (_a) {
            var _b;
            var p = _a.p, e = _a.e;
            renderAnimation({
                parent: parentRef.current,
                overlay: overlayRef.current,
                scroll: scrollRef.current,
                p: p,
                e: e,
            });
            if (p === 0 && ((_b = timeline === null || timeline === void 0 ? void 0 : timeline.timeline) === null || _b === void 0 ? void 0 : _b.isReversed)) {
                if (isUnmountOnClose) {
                    setCanRender(false);
                }
                setIsVisible(false);
                onHidden === null || onHidden === void 0 ? void 0 : onHidden();
            }
        },
    });
    // animate the modal
    useEffect(function () {
        if (isOpen) {
            setCanRender(true);
            setIsVisible(true);
            timeline === null || timeline === void 0 ? void 0 : timeline.play();
        }
        else {
            timeline === null || timeline === void 0 ? void 0 : timeline.reverse();
        }
    }, [isOpen, timeline]);
    return canRender ? (React.createElement(Portal, { node: parentNode },
        React.createElement("div", { ref: parentRef, className: cn(className, prefixedClasNames('base-modal', !isVisible && 'invisible')), style: style, role: "dialog", "aria-modal": true, "aria-hidden": !isVisible },
            React.createElement("div", __assign({ ref: overlayRef }, overlayProps, { className: cn(overlayProps === null || overlayProps === void 0 ? void 0 : overlayProps.className, prefixedClasNames('base-modal__overlay')) })),
            React.createElement("div", __assign({ ref: scrollRef }, scrollAreaProps, { className: cn(scrollAreaProps === null || scrollAreaProps === void 0 ? void 0 : scrollAreaProps.className, prefixedClasNames('base-modal__scroll-area')) }),
                React.createElement("div", __assign({ ref: wrapperRef }, wrapperProps, { className: cn(wrapperProps === null || wrapperProps === void 0 ? void 0 : wrapperProps.className, prefixedClasNames('base-modal__wrapper')) }),
                    children,
                    React.createElement(CloseButton, { onClick: onClose })))))) : null;
};
//# sourceMappingURL=index.js.map